import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import InputLabel from '@material-ui/core/InputLabel';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import FormPopup from 'components/Operational/FormPopup';
import Button from 'components/Button';

import fieldSelection from 'components/Forms/Fields';
import DetailField from 'components/Details';
import Image from 'components/Image';

import styles from './index.module.scss';
import cn from 'classnames';
import startCase from 'lodash/startCase';
import find from 'lodash/find';
import { DeleteIcon } from 'Icons';
import staticText from 'static/text_display.json';
import { formatMessage } from 'utils/methods/helpers';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import isEmpty from 'lodash/isEmpty';
import { DEFAULT_OPTION_LABEL_KEY } from 'utils/constants';
import { useTranslation } from 'react-i18next';
import { Box, SvgIcon } from '@material-ui/core';
import AddCircleOutlineSharpIcon from '@material-ui/icons/AddCircleOutlineSharp';
import cloneDeep from 'lodash/cloneDeep';

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    marginBottom: '1rem',
    alignItems: 'baseline',
  },
  subFormContainerBackground: {
    background: theme.palette.background.default,
  },
  header: {
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.text.primary}`,
    marginTop: '0.5rem',
  },
  values: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  value: {
    width: '17%',
    textAlign: 'center',
    padding: '0.5rem 0',
  },
  cell: {
    width: '17%',
    textAlign: 'center',
    padding: '0.5rem 0',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingBottom: '0.5rem',
  },
  btnContainer: {
    width: '75%',
    display: 'flex',
    justifyContent: 'space-evenly',
    paddingTop: '0.5rem',
  },
  img: {
    boxSizing: 'border-box',
  },
  icon: {
    '&:hover': {
      background: 'transparent',
    },
  },
  disabled: {
    background: theme.palette.text.disabled,
    '&:hover': {
      background: theme.palette.text.disabled,
      cursor: 'pointer',
    },
  },
  mySvgStyle: {
    height: '25px', //*[@id="Layer_1"]/g/path
    width: '25px',
    cursor: 'pointer',
    '& [id="Layer_1"]': {
      color: `${theme.palette.text.primary} !important`,
      '& [class*="st0"]': {
        fill: `${theme.palette.text.primary} !important`,
      },
    },
  },
}));

const Form = ({
  fields,
  defaultValues,
  submit,
  exception,
  cancel,
  requiredFields,
  setErrorCondition,
}) => {
  const cpf = cloneDeep(fields);

  const { t } = useTranslation();
  const classes = useStyles();

  const [values, setValues] = useState(defaultValues);

  const [touched, setTouched] = useState({});
  const [types, setTypes] = useState(
    cpf.reduce(
      (acc, item) => ({
        ...acc,
        [item.field]: item.type,
      }),
      {},
    ),
  );
  const [errors, setErrors] = useState({});

  const getFieldsDisplayData = () => {
    return cpf.reduce(
      (acc, item) => ({
        ...acc,
        [item.field]: item.display,
      }),
      {},
    );
  };

  const transformCpf = useCallback(() => {
    cpf.forEach(option => {
      if (option.field === 'eventType') {
        option['fetchFilters'] = {
          archived: false,
          category: values.eventCategory,
        };
        option['resetWhen'] = [
          {
            value: values.eventCategory,
            condition: val => {
              return val;
            },
          },
        ];
        option['disabled'] = values.eventCategory === '';
      }
    });
  }, [cpf, values.eventCategory]);

  transformCpf();
  const displayData = getFieldsDisplayData();

  const setFieldTouched = key => {
    setTouched({ ...touched, [key]: true });
  };
  const handleChange = event => {
    const key = event.target.name;
    const { value } = event.target;
    const { customNameValue } = event.target;
    const { customName } = event.target;

    if (key === exception) {
      const newTypes = Object.keys(value).reduce(
        (acc, item) => ({
          ...acc,
          [item]: 'text',
        }),
        {},
      );
      setTypes({ ...types, ...newTypes });
      setValues({
        ...values,
        ...value,
        [key]: value,
      });
    } else {
      const { type } = find(cpf, ['field', customName || key]);
      setTypes({ ...types, [customName || key]: type });
      setValues({
        ...values,
        [customName || key]: customNameValue || value,
      });
    }
  };

  const setFieldValue = (key, value) => {
    setValues({ ...values, [key]: value });
  };

  const onAddItem = () => {
    const addedValues = cpf.reduce((acc, item) => {
      if (item.field === exception) {
        return {
          ...acc,
          [item.field]: {
            value: values[item.field][item.field],
            type: types[item.field],
            display: displayData[item.field],
          },
        };
      } else {
        const displayValue =
          !isEmpty(item.options) && values[item.field]
            ? item.options.find(v => v.id === values[item.field])[
                item.optionLabelKey || DEFAULT_OPTION_LABEL_KEY
              ] || values[item.field]
            : values[item.field]; // normally this should be a string but if the field value is array then translate wont work

        let translatedDisplayValue = '';
        if (Array.isArray(displayValue)) {
          translatedDisplayValue = '';
        } else if (
          typeof displayValue === 'object' &&
          item.type === 'async-select'
        ) {
          translatedDisplayValue = t(displayValue[item.optionLabelKey]);
        } else {
          translatedDisplayValue = t(displayValue);
        }

        return {
          ...acc,
          [item.field]: {
            value:
              typeof displayValue === 'object' && item.type === 'async-select'
                ? values[item.field][item.optionLabelKey]
                : values[item.field],
            displayValue: translatedDisplayValue,
            type: types[item.field],
            display: displayData[item.field],
            doNotShow: !values[item.field], // if there is no value then donotshow becomes true
          },
        };
      }
    }, {});
    let tempErrors = {};
    let tempTouched = {};
    //need to refactore below lines
    // eslint-disable-next-line
    requiredFields.map(keyName => {
      if (values[keyName] === '') {
        tempErrors = { ...tempErrors, [keyName]: 'required_label' };
        tempTouched = { ...tempTouched, [keyName]: true };
      }
    });
    // custom error conditions
    if (Array.isArray(setErrorCondition)) {
      setErrorCondition.forEach(f => {
        // only check if it applies to the selected evidence
        if (
          f.dependentKeys.includes(values.type) &&
          f?.condition(values[f.key])
        ) {
          tempErrors = { ...tempErrors, [f.key]: f.errorMsg };
          tempTouched = { ...tempTouched, [f.key]: true };
        }
      });
    }
    setTouched(tempTouched);
    setErrors(tempErrors);
    if (
      Object.keys(tempErrors).length === 0 &&
      Object.keys(tempTouched).length === 0
    ) {
      submit(addedValues);
    }
  };

  const onAsyncChange = (selectedValue, name) => {
    const key = name;
    const value = selectedValue;
    if (key === exception) {
      const newTypes = Object.keys(value).reduce(
        (acc, item) => ({
          ...acc,
          [item]: 'text',
        }),
        {},
      );
      setTypes({ ...types, ...newTypes });
      setValues({
        ...values,
        ...value,
        [key]: value,
      });
    } else {
      const { type } = find(cpf, ['field', key]);
      setTypes({ ...types, [key]: type });
      setValues({
        ...values,
        [key]: value,
      });
    }
  };
  const props = {
    values,
    touched,
    errors,
    setFieldTouched,
    handleChange,
    setFieldValue,
    onAsyncChange,
    setValues,
  };

  return (
    <div
      className={cn(styles.formContainer, classes.subFormContainerBackground)}
    >
      <div className={styles.form}>
        {cpf
          .map(m => {
            if (values.showDynamicText && m.type === 'dynamic-text-generator') {
              return {
                ...m,
                hidden: !values.showDynamicText,
              };
            }
            return m;
          })
          .map((field, index) =>
            fieldSelection({ item: field, key: index, id: index, props }),
          )}
        <section className={classes.btnContainer}>
          <Button
            type="button"
            text={'Cancel'}
            onClick={cancel}
            size="l"
            color="secondary"
            outline={true}
          />
          <Button
            type="button"
            onClick={onAddItem}
            text={'Submit'}
            size="l"
            color="primary"
          />
        </section>
      </div>
    </div>
  );
};

Form.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.object),
  defaultValues: PropTypes.object.isRequired,
  submit: PropTypes.func.isRequired,
  exception: PropTypes.string,
  cancel: PropTypes.func.isRequired,
  requiredFields: PropTypes.array,
  setErrorCondition: PropTypes.array,
};

const SubForm = ({
  subFields,
  defaultValues,
  label,
  onChange,
  required,
  exception,
  value,
  subFormHeader,
  requiredFields,
  titleKey = '',
  info,
  cellClassName,
  disabled = false,
  setErrorCondition,
  item,
}) => {
  const { t } = useTranslation();
  const classes = useStyles(disabled);
  const [showPopup, setShowPopup] = useState(false);
  const [allFormValues, setallFormValues] = useState(value || []);

  useEffect(() => {
    if (value) {
      setallFormValues(value);
    }
  }, [value]);

  const onAddItem = () => {
    setShowPopup(true);
  };
  const onClickSubmit = addedValues => {
    setallFormValues([...allFormValues, addedValues]);
    onChange([...allFormValues, addedValues]);
    setShowPopup(false);
  };

  const removeAddedItem = text => {
    const remainingItems = allFormValues.filter(item => item !== text);
    setallFormValues(remainingItems);
    onChange(remainingItems);
  };
  const closePopup = () => {
    setShowPopup(false);
  };
  const getFieldsToDisplay = cpf => {
    let fieldsToShow = [];
    for (const [key, value] of Object.entries(cpf)) {
      if (!(value.display && value.display === 'none')) {
        fieldsToShow = [...fieldsToShow, key];
      }
    }
    return fieldsToShow;
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.labelContainer}>
          <InputLabel shrink required={required}>
            {t(label)}
            {info && <Tooltip text={info} />}
          </InputLabel>
          <Image
            title={subFormHeader}
            icon={<AddCircleOutlineSharpIcon />}
            onClick={onAddItem}
            className={cn(classes.img, { [classes.disabled]: disabled })}
            text={
              item?.buttonLabel
                ? `${t(item?.buttonLabel)}`
                : `${t('Add')} ${t(label)}`
            }
          />
        </div>

        {allFormValues.length > 0 && (
          <Box component={'section'} width={'inherit'}>
            <section className={classes.values}>
              {getFieldsToDisplay(allFormValues[0]).map((item, key) => {
                if (
                  allFormValues[0][item].doNotShow &&
                  !allFormValues[0][item].value
                )
                  return null;
                return (
                  <div
                    className={cn(classes.value, classes.header, cellClassName)}
                    key={key}
                  >
                    {t(startCase(item))}
                  </div>
                );
              })}
            </section>
            {allFormValues.map((items, itemIndex) => (
              <section className={classes.values} key={itemIndex}>
                {getFieldsToDisplay(allFormValues[0]).map((item, index) => {
                  if (
                    allFormValues[0][item].doNotShow &&
                    !allFormValues[0][item].value
                  )
                    return null;

                  return (
                    <div
                      className={cn(classes.value, classes.cell, cellClassName)}
                      key={index}
                    >
                      <DetailField
                        type={items[item]?.type}
                        value={items[item].displayValue || items[item].value}
                      />
                    </div>
                  );
                })}
                {!disabled && (
                  <IconButton
                    color="secondary"
                    component="div"
                    className={classes.icon}
                    onClick={() => removeAddedItem(items)}
                    title={t(
                      formatMessage({
                        message: staticText.general.delete_value,
                        value: titleKey ? items[titleKey].value : titleKey,
                      }),
                    )}
                  >
                    <SvgIcon className={classes.mySvgStyle}>
                      <DeleteIcon />
                    </SvgIcon>
                  </IconButton>
                )}
              </section>
            ))}
          </Box>
        )}
      </div>
      {!disabled && showPopup && (
        <FormPopup
          headerTitle={t(item?.buttonLabel || subFormHeader)}
          closePopup={closePopup}
          form={
            <Form
              fields={subFields}
              defaultValues={defaultValues}
              submit={onClickSubmit}
              cancel={closePopup}
              exception={exception}
              requiredFields={requiredFields}
              setErrorCondition={setErrorCondition}
            />
          }
        />
      )}
    </>
  );
};

SubForm.propTypes = {
  subFields: PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  item: PropTypes.object,
  exception: PropTypes.string,
  value: PropTypes.array,
  subFormHeader: PropTypes.string,
  requiredFields: PropTypes.array,
  setErrorCondition: PropTypes.array,
  titleKey: PropTypes.string,
  info: InfoPropTypes,
  cellClassName: PropTypes.string,
};
export default SubForm;
